import Dialog from "@rio-cloud/rio-uikit/Dialog";
import ReceiptForm, { FormState, NameInputChange } from "./form";
import { useFormReceipt } from "./useFormReceipt";
import React, { useEffect, useState } from "react";
import { useT } from "@hooks";
import type { RioTrip } from "@types";
import DialogCreateType from "../Create";
import {
  createBranch,
  createCategory,
  createSegments,
  createSubCategories,
  createSuppliers,
  createTpgTrip,
  inactivateBranch,
  inactivateSegments,
  inactivateSuppliers,
  inactivateCategory,
  inactivateSubCategories,
} from "@services";
import { Notification } from "@rio-cloud/rio-uikit";
import ReactivateDialog, { InactiveItem } from "../Reactivate";
import { InactiveSelectOption } from "./select";
import { useGetStoredSelectedVehicles } from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { normalizeTpgTripDescription, TPG_TRIP_DESCRIPTION_MAX_LENGTH } from "@utils/rioTripOptions";

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (data: FormState, callBack?: () => void, closeModal?: boolean) => void;
  title: string;
  selected?: FormState & { id?: string };
  selectedTrip?: RioTrip | null;
  onViewRecurrence?: () => void;
  handleFile?: (file: File) => void;
  handleClearFile: () => void;
  fileName?: string;
  changeFileName: (fileName: string) => void;
  isCreate?: boolean;
  onDownloadFile?: (transactionId: string) => void;
  hasNewAttachment?: boolean;
}

const defaultOption = {
  open: false,
  type: "",
  title: "",
  value: "",
  placeholder: "",
};

interface Option {
  [x: string]: { placeholder: string; title: string };
}

const DialogReceipt = ({
  show,
  fileName,
  onClose,
  onSave,
  title,
  selected,
  selectedTrip,
  onViewRecurrence,
  handleFile,
  handleClearFile,
  changeFileName,
  isCreate,
  onDownloadFile,
  hasNewAttachment,
}: Props) => {
  const t = useT();
  const {
    branchs,
    segments,
    supplier,
    drivers,
    categories,
    subCategories,
    vehicles,
    trips,
    tripsData,
    fetchTrips,
    setSelectedTrip,
    tpgTrips,
    addTpgTrip,
    form,
    setForm,
    fetchBranchs,
    fetchSegments,
    fetchSupplier,
    fetchCategories,
    fetchSubCategories,
    initialDefaultForm,
  } = useFormReceipt();
  const vehiclesSelected = useGetStoredSelectedVehicles();

  useEffect(() => {
    if (selected) {
      setForm(selected);
    }
  }, [selected]);

  useEffect(() => {
    setSelectedTrip(selectedTrip ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrip]);

  const [dialogOption, setDialogOption] = useState(defaultOption);
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [duplicateAlertMessage, setDuplicateAlertMessage] = useState("");
  const [reactivateItem, setReactivateItem] = useState<InactiveItem | null>(null);
  const [showReactivate, setShowReactivate] = useState(false);

  const entityLabels: Record<string, string> = {
    branch: t("entity.branch"),
    segment: t("entity.segment"),
    supplier: t("entity.client"),
    category: t("entity.category"),
    subcategory: t("entity.subcategory"),
  };

  const reactivateHandlers: Record<string, (id: number, activate: boolean) => Promise<any>> = {
    branch: inactivateBranch,
    segment: inactivateSegments,
    supplier: inactivateSuppliers,
    category: inactivateCategory,
    subcategory: inactivateSubCategories,
  };

  const refetchHandlers: Record<string, () => Promise<void>> = {
    branch: fetchBranchs,
    segment: fetchSegments,
    supplier: fetchSupplier,
    category: fetchCategories,
    subcategory: fetchSubCategories,
  };

  const handleInactiveSelect = (item: InactiveSelectOption, fieldName: string) => {
    setReactivateItem({
      id: item.id as string,
      name: item.label as string,
      fieldName,
    });
    setShowReactivate(true);
  };

  const handleReactivate = async (id: string, fieldName: string) => {
    const handler = reactivateHandlers[fieldName];
    if (!handler) return;

    await handler(Number(id), true);
    await refetchHandlers[fieldName]?.();

    setForm((prev) => ({
      ...prev,
      [fieldName]: id,
    }));

    Notification.success(t("receipt.reactivated", { entity: entityLabels[fieldName] }));
  };

  const handleReactivateCancel = () => {
    setShowReactivate(false);
    setReactivateItem(null);
  };

  const onCreateNewOption = (name: NameInputChange) => {
    // Operacao/Segmento usa dialog especifico com 2 campos
    // if (name === 'segment') {
    //     setShowOperationDialog(true);
    //     return;
    // }

    const opt: Option = {
      branch: {
        placeholder: t("receipt.createNew.branch.placeholder"),
        title: t("receipt.createNew.branch.title"),
      },
      supplier: {
        placeholder: t("receipt.createNew.supplier.placeholder"),
        title: t("receipt.createNew.supplier.title"),
      },
      category: {
        placeholder: t("receipt.createNew.category.placeholder"),
        title: t("receipt.createNew.category.title"),
      },
      subcategory: {
        placeholder: t("receipt.createNew.subcategory.placeholder"),
        title: t("receipt.createNew.subcategory.title"),
      },
      segment: {
        placeholder: t("receipt.createNew.segment.placeholder"),
        title: t("receipt.createNew.segment.title"),
      },
      trip: {
        placeholder: t("receipt.createNew.trip.placeholder"),
        title: t("receipt.createNew.trip.title"),
      },
    };

    setDialogOption({
      open: true,
      placeholder: opt[name]?.placeholder,
      title: opt[name]?.title,
      type: name,
      value: "",
    });
  };

  const handleSaveBranch = async (value: string) => {
    try {
      const response = await createBranch({
        name: value,
      });
      fetchBranchs();
      setForm({
        ...form,
        branch: response.data.id.toString(),
      });
      setShowDuplicateAlert(false);
      setDialogOption(defaultOption);
      Notification.success(t("receipt.notification.branch.created"));
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setDuplicateAlertMessage(t("receipt.notification.branch.duplicate"));
        setShowDuplicateAlert(true);
      } else {
        Notification.error(t("receipt.notification.branch.error"));
      }
    }
  };

  const handleSaveOperation = async (value: string) => {
    try {
      const response = await createSegments(value);
      fetchSegments();
      setForm({
        ...form,
        segment: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success(t("receipt.notification.segment.created"));
    } catch {
      Notification.error(t("receipt.notification.segment.error"));
    }
  };

  const handleSaveAccounts = async (value: string) => {
    try {
      const response = await createSuppliers({
        name: value,
        type: "CLIENTE",
      });
      fetchSupplier();
      setForm({
        ...form,
        supplier: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success(t("receipt.notification.supplier.created"));
    } catch {
      Notification.error(t("receipt.notification.supplier.error"));
    }
  };

  const handleSaveCategory = async (value: string) => {
    try {
      const response = await createCategory({
        name: value,
        type: "RECEITA",
      });
      fetchCategories();
      setForm({
        ...form,
        category: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success(t("receipt.notification.category.created"));
    } catch {
      Notification.error(t("receipt.notification.category.error"));
    }
  };

  const handleSaveSubCategory = async (value: string) => {
    try {
      const response = await createSubCategories(form.category, { name: value });
      fetchSubCategories();
      setForm({
        ...form,
        subcategory: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success(t("receipt.notification.subcategory.created"));
    } catch {
      Notification.error(t("receipt.notification.subcategory.error"));
    }
  };

  const handleSaveTrip = async (value: string) => {
    try {
      const typed = normalizeTpgTripDescription(value);
      const created = await createTpgTrip(typed);
      const description = created?.description ?? typed;
      addTpgTrip({ ...created, description });
      setForm((prev) => ({ ...prev, trip: description }));
      setShowDuplicateAlert(false);
      setDialogOption(defaultOption);
      Notification.success(t("receipt.notification.trip.created"));
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setDuplicateAlertMessage(t("receipt.notification.trip.duplicate"));
        setShowDuplicateAlert(true);
      } else {
        Notification.error(t("receipt.notification.trip.error"));
      }
    }
  };

  const handleSave = (value: string) => {
    if (dialogOption.type === "trip") {
      handleSaveTrip(value);
      return;
    }

    if (dialogOption.type === "branch") {
      handleSaveBranch(value);
      return;
    }

    if (dialogOption.type === "supplier") {
      handleSaveAccounts(value);
      return;
    }

    if (dialogOption.type === "category") {
      handleSaveCategory(value);
      return;
    }

    if (dialogOption.type === "subcategory") {
      handleSaveSubCategory(value);
      return;
    }

    if (dialogOption.type === "segment") {
      handleSaveOperation(value);
    }
  };

  const getExistingNames = () => {
    if (dialogOption.type === "category") return categories.map((c) => c.label as string);
    if (dialogOption.type === "subcategory") return subCategories.map((s) => s.label as string);
    if (dialogOption.type === "trip") return tpgTrips.map((trip) => trip.id);
    return undefined;
  };

  const getDuplicateNameMessage = () => {
    if (dialogOption.type === "category") return t("receipt.notification.category.duplicate");
    if (dialogOption.type === "subcategory") return t("receipt.notification.subcategory.duplicate");
    if (dialogOption.type === "trip") return t("receipt.notification.trip.duplicateInline");
    return undefined;
  };

  const handleDefaultForm = () => {
    setForm(initialDefaultForm);
  };

  const handleSaveAndCreateNew = (data: FormState) => {
    onSave(
      data,
      () => {
        handleDefaultForm();
        setFormInstanceKey((prev) => prev + 1);
      },
      false
    );
  };

  const handleDefault = () => {
    onClose();
    handleDefaultForm();
  };

  useEffect(() => {
    if (isCreate && !selected && vehiclesSelected?.length === 1) {
      setForm((prev) => ({
        ...prev,
        vehicle: vehiclesSelected?.[0],
      }));
    }
  }, [isCreate]);

  return (
    <React.Fragment>
      <Dialog
        show={show && !dialogOption.open && !showReactivate}
        title={<span className="text-medium text-size-16">{title}</span>}
        body={
          <ReceiptForm
            key={formInstanceKey}
            form={form}
            vehicles={vehicles}
            trips={trips}
            tripsData={tripsData}
            onTripFiltersChange={fetchTrips}
            setForm={setForm}
            branchs={branchs}
            segments={segments}
            suppliers={supplier}
            drivers={drivers}
            categories={categories}
            subCategories={subCategories}
            openFormModal={onClose}
            openNewOptionModal={() => {}}
            onSave={(form) => onSave(form, handleDefaultForm)}
            onSaveAndCreateNew={handleSaveAndCreateNew}
            onCreateNewOption={onCreateNewOption}
            onCancel={handleDefault}
            isEdit={!!selected}
            onViewRecurrence={onViewRecurrence}
            selected={selected}
            handleFile={handleFile}
            handleClearFile={handleClearFile}
            fileName={fileName}
            changeFileName={changeFileName}
            onInactiveSelect={handleInactiveSelect}
            onDownloadFile={onDownloadFile}
            transactionId={selected?.id}
            hasNewAttachment={hasNewAttachment}
          />
        }
        onClose={handleDefault}
        showCloseButton
      />
      <DialogCreateType
        title={dialogOption.title}
        show={dialogOption.open}
        placeholder={dialogOption.placeholder}
        onClose={() => {
          setDialogOption(defaultOption);
          setShowDuplicateAlert(false);
        }}
        onSave={handleSave}
        showDuplicateAlert={["branch", "trip"].includes(dialogOption.type) && showDuplicateAlert}
        duplicateAlertMessage={duplicateAlertMessage}
        onInputChange={() => setShowDuplicateAlert(false)}
        existingNames={getExistingNames()}
        duplicateNameMessage={getDuplicateNameMessage()}
        requireTrimmedValue={dialogOption.type === "trip"}
        maxLength={dialogOption.type === "trip" ? TPG_TRIP_DESCRIPTION_MAX_LENGTH : undefined}
      />

      <ReactivateDialog
        show={showReactivate}
        item={reactivateItem}
        entityLabel={reactivateItem ? entityLabels[reactivateItem.fieldName] || "" : ""}
        transactionLabel="receitas"
        onReactivate={handleReactivate}
        onCancel={handleReactivateCancel}
      />
    </React.Fragment>
  );
};

export default DialogReceipt;
